import { can } from "./permissions.js";
import type { MerchantSessionContext, NavigationItem, RouteDefinition } from "./types.js";

export const ROUTES: RouteDefinition[] = [
  { id: "dashboard", label: "Dashboard", path: "/dashboard", requiredPermissions: [] },
  { id: "onboarding", label: "Onboarding", path: "/onboarding", requiredPermissions: [] },
  {
    id: "requests",
    label: "Requests",
    path: "/requests-list",
    requiredPermissions: ["review_requests"],
  },
  {
    id: "catalog-pricing",
    label: "Catalog & Pricing",
    path: "/products-list",
    requiredPermissions: [],
    children: [
      { id: "products", label: "Products", path: "/products-list", requiredPermissions: [] },
      {
        id: "competitors",
        label: "Competitors",
        path: "/competitors-list",
        requiredPermissions: ["manage_pricing"],
      },
      {
        id: "mappings",
        label: "Mappings",
        path: "/mappings",
        requiredPermissions: ["read_evidence"],
      },
    ],
  },
  {
    id: "store-configuration",
    label: "Store configuration",
    path: "/pricing-policies",
    requiredPermissions: [],
    children: [
      {
        id: "policies",
        label: "Policies",
        path: "/pricing-policies",
        requiredPermissions: ["manage_settings"],
      },
      {
        id: "settings",
        label: "Widget",
        path: "/settings?tab=widget",
        requiredPermissions: ["manage_settings"],
      },
      {
        id: "notifications-sla",
        label: "Notifications & SLA",
        path: "/settings?tab=notifications",
        requiredPermissions: ["manage_settings"],
      },
      {
        id: "plans-pricing",
        label: "Plans & Pricing",
        path: "/plans-pricing",
        requiredPermissions: ["manage_billing"],
      },
    ],
  },
  {
    id: "administration",
    label: "Administration",
    path: "/team-access",
    requiredPermissions: [],
    children: [
      { id: "team", label: "Team", path: "/team-access", requiredPermissions: ["manage_team"] },
      { id: "audit", label: "Audit", path: "/audit-history", requiredPermissions: ["audit.read"] },
    ],
  },
  {
    id: "support-feedback",
    label: "Support & Feedback",
    path: "/support-feedback",
    requiredPermissions: [],
  },
  { id: "faq", label: "FAQ", path: "/faq", requiredPermissions: [] },
];

export function visibleNavigation(context: MerchantSessionContext): NavigationItem[] {
  return ROUTES.flatMap((route) => toVisibleRoute(route, context));
}

export function canEnterRoute(route: RouteDefinition, context: MerchantSessionContext): boolean {
  if (context.user.status !== "ACTIVE") {
    return route.id === "onboarding";
  }

  return routeIsVisible(route, context);
}

export function routeDefinitionForNavigation(navId: unknown): RouteDefinition | undefined {
  if (typeof navId !== "string") return undefined;
  return findRouteDefinition(navId, ROUTES);
}

function routeIsVisible(route: RouteDefinition, context: MerchantSessionContext): boolean {
  return (
    route.requiredPermissions.length === 0 ||
    route.requiredPermissions.some((permission) => can(context.user, permission))
  );
}

function findRouteDefinition(
  navId: string,
  routes: RouteDefinition[],
): RouteDefinition | undefined {
  for (const route of routes) {
    if (route.id === navId) return route;
    const child = route.children?.find((item) => item.id === navId);
    if (child !== undefined) return child;
  }

  return undefined;
}

function toVisibleRoute(route: RouteDefinition, context: MerchantSessionContext): NavigationItem[] {
  const children = route.children?.filter((child) => routeIsVisible(child, context));
  if (children !== undefined) {
    if (children.length === 0) return [];
    return [{ ...route, path: children[0]!.path, children }];
  }

  return routeIsVisible(route, context) ? [route] : [];
}
